<template>
  <div>
    <!-- Loading -->
    <LoadingOverlay :active="isLoading" />

    <!-- Container -->
    <div class="redirect-container">
      <!-- Error Alert Modal -->
      <div v-if="errorMessage" class="alert-modal">
        <div class="alert-content">
          <img src="/static/icons/error-icon.svg" class="alert-icon" />
          <p class="alert-message">{{ errorMessage }}</p>
          <sc-button
            class="w-100"
            style="height: 45px"
            variant="primary"
            @click="navigateToLogin()"
          >
            กลับไปหน้า Login
          </sc-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import ScButton from "@/lib/components/ScButton"

// Dependencies
import { baanRueJaiClient, backendAdminClient } from "@/dependencies"

// Utils
import { toCamelCaseObject } from "@/lib/util"
import { Auth, setCookie, unsetCookie } from "@/lib/cookie"
import { FCM_CONFIG } from "@/config/fcm"
import { getMessagingIfSupported } from "@/setup/firebaseMessaging"
import { deleteToken, getToken } from "firebase/messaging"
import LoadingOverlay from "@/lib/components/LoadingOverlay"
import {
  AuthMicrosoftActionEnum,
  getAuthMicrosoftErrorMessage,
} from "../auth-microsoft"

export default {
  name: "AuthMicrosoftPage",
  components: {
    LoadingOverlay,
    ScButton,
  },
  data() {
    return {
      isLoading: false,
      errorMessage: "",
    }
  },
  computed: {
    passport() {
      return this.$route.params.passport
    },
  },
  async mounted() {
    await this.handleAuthAction()
  },
  methods: {
    async fetchAdminProfiles() {
      try {
        // Fetch admin profiles
        const response = await backendAdminClient.admin.getProfiles()

        const data = response && response.data
        const result = data && data.result

        if (!result) {
          this.errorMessage = "ไม่พบข้อมูลโปรไฟล์ กรุณาติดต่อผู้ดูแลระบบ"
          return
        }

        this.saveAdminProfileToStorage(result)
      } catch (error) {
        // Set error message
        this.errorMessage =
          error.message || "เกิดข้อผิดพลาดในการดึงข้อมูลโปรไฟล์"

        // Log the error for debugging
        // eslint-disable-next-line no-console
        console.error("fetchAdminProfile error:", error)
      }
    },
    saveAdminProfileToStorage(result) {
      // Convert result to camelCase
      const adminProfile = toCamelCaseObject(result)

      // Destructure adminProfile
      const {
        firstName,
        lastName,
        role,
        adminRole,
        id,
        projects = [],
      } = adminProfile

      // Destructure adminRole
      const { roleId, features = [] } = adminRole || {}

      // Validate required fields
      if (!roleId) {
        this.errorMessage = "ไม่พบสิทธิ์การเข้าถึง กรุณาติดต่อผู้ดูแลระบบ"
        return
      }
      if (!features || features.length === 0) {
        this.errorMessage = "ไม่พบฟีเจอร์การใช้งาน กรุณาติดต่อผู้ดูแลระบบ"
        return
      }

      // Save admin profile
      localStorage.setItem("adminFirstName", firstName)
      localStorage.setItem("adminLastName", lastName)
      localStorage.setItem("adminRole", role)
      localStorage.setItem("adminRolePermission", JSON.stringify(adminRole))
      localStorage.setItem("adminRoleId", roleId)
      localStorage.setItem("adminId", id)
      localStorage.setItem(
        "adminProjects",
        projects.map((project) => project.id).join(),
      )

      // Navigate to target page
      this.navigateToTarget()
    },
    async handleAuthAction() {
      this.isLoading = true
      this.errorMessage = ""

      try {
        // Destructure passport
        const { action, code, credential } =
          this.decodePassport(this.passport) || {}

        // Validate the action
        switch (action) {
          case AuthMicrosoftActionEnum.Login: {
            if (!credential) {
              this.errorMessage =
                "ไม่พบข้อมูลการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง"
              return
            }

            const { accessToken, refreshToken, sessionId } = credential

            localStorage.setItem("microsoftSessionId", sessionId)

            setCookie(Auth.AccessToken, accessToken)
            setCookie(Auth.RefreshToken, refreshToken)

            backendAdminClient.setToken(accessToken)
            baanRueJaiClient.setAuthorizationToken(accessToken)

            if (this.isLoggedInFromRjServices()) {
              try {
                this.broadcastCredentials(refreshToken, accessToken)
              } catch (error) {
                // Ignore broadcast failures during login redirect.
              }
            }

            await this.fetchAdminProfiles()
            break
          }

          case AuthMicrosoftActionEnum.Logout:
            // Unsubscribe from FCM
            await this.unsubscribeFcm()

            // Clear localStorage
            localStorage.removeItem("microsoftSessionId")
            localStorage.removeItem("adminName")
            localStorage.removeItem("adminRole")
            localStorage.removeItem("adminRolePermission")
            localStorage.removeItem("adminProjects")
            localStorage.removeItem("accessToken")
            localStorage.removeItem("refreshToken")

            // Clear cookies
            unsetCookie(Auth.AccessToken)
            unsetCookie(Auth.RefreshToken)

            // Navigate to login page
            this.navigateToLogin()
            break

          case AuthMicrosoftActionEnum.Error:
            this.errorMessage = getAuthMicrosoftErrorMessage(code)
            break

          default:
            this.errorMessage = "เกิดข้อผิดพลาด กรุณาติดต่อผู้ดูแลระบบ"
            break
        }
      } catch (error) {
        this.errorMessage =
          error.message || "เกิดข้อผิดพลาด กรุณาติดต่อผู้ดูแลระบบ"
      } finally {
        this.isLoading = false
      }
    },
    decodePassport(encoded) {
      const normalized = encoded.replace(/-/g, "+").replace(/_/g, "/")
      const paddingLength = (4 - (normalized.length % 4)) % 4
      const padded = normalized.padEnd(normalized.length + paddingLength, "=")
      const binary = window.atob(padded)
      const bytes = Uint8Array.from(binary, (character) =>
        character.charCodeAt(0)
      )
      const jsonString = new TextDecoder("utf-8").decode(bytes)

      return JSON.parse(jsonString)
    },
    async unsubscribeFcm() {
      try {
        const messaging = await getMessagingIfSupported()

        if (messaging) {
          const registration = await navigator.serviceWorker.register(
            `${window.location.origin}/static/firebase-messaging-sw.js`,
          )

          // getToken() binds our service worker registration to the messaging
          // instance so deleteToken() does not try to register the SDK default
          // /firebase-messaging-sw.js, which this app does not serve.
          await getToken(messaging, {
            vapidKey: FCM_CONFIG.VAPID_KEY,
            serviceWorkerRegistration: registration,
          })

          // Unsubscribe from FCM
          await deleteToken(messaging)
        }
      } catch (error) {
        // Ignore FCM unsubscribe failures so logout can continue.
      }

      // Clear token
      baanRueJaiClient.setAuthorizationToken()
    },
    navigateToTarget() {
      const targetRoute = this.$route.query.redirect
        ? { path: this.$route.query.redirect }
        : { name: "Dashboard" }

      // Navigate to the target route
      this.$router.push(targetRoute)
    },
    navigateToLogin() {
      this.$router.push({ name: "Login" })
    },
    isLoggedInFromRjServices() {
      // Check if rj services such as RJ Staff App is using this login page
      return (
        window.authJsHandler &&
        typeof window.authJsHandler.postMessage === "function"
      )
    },
    broadcastCredentials(refreshToken, accessToken) {
      if (!refreshToken || !accessToken) {
        // eslint-disable-next-line no-console
        console.error("Refresh token or access token is empty")
        return
      }

      if (!this.isLoggedInFromRjServices()) {
        // eslint-disable-next-line no-console
        console.error("Only allowed to broadcast credentials to rj services")
        return
      }

      window.authJsHandler.postMessage(
        JSON.stringify({
          refresh_token: refreshToken,
          access_token: accessToken,
        }),
      )
    },
  },
}
</script>

<style scoped>
.redirect-container {
  width: 100vw !important;
  height: 100vh !important;
  display: flex;
  flex-direction: column;
  background-image: url("/static/pictures/background.webp");
  background-size: cover;
  background-position: center;
  position: relative; /* ต้องมีเพื่อให้ ::before ทำงาน */
}

.redirect-container::before {
  content: "";
  position: absolute; /* หรือ fixed ถ้าต้องการครอบเต็ม viewport */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 0;
  pointer-events: none;
}

/* Alert Modal */
.alert-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}
.alert-content {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 360px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  text-align: center;
}
.alert-icon {
  width: 100%;
  margin-bottom: 16px;
}
.alert-message {
  font-size: 16px;
  color: #333;
  margin-bottom: 28px;
  white-space: pre-wrap;
  text-align: center;
}
</style>
